/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, lazy, Suspense } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import EmptyState from '@atlaskit/empty-state/empty-state';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import FileIcon from '@atlaskit/icon/core/file';
import OpenFolderIcon from '@atlaskit/icon/core/folder-open';
import OfficeBuildingIcon from '@atlaskit/icon/core/office-building';
import DocumentsIcon from '@atlaskit/icon/core/pages';
import QuoteIcon from '@atlaskit/icon/core/quotation-mark';
import Link from '@atlaskit/link/link';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import BlogObject from '@atlaskit/object/blog';
import PageObject from '@atlaskit/object/page';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';
import TimeAgo from '@atlassian/mpt-timeago';

import { useAIAnswerContext } from '../../../../ai-answer-dialog/ai-answer-context';
import { type CuratedDefinitionScopeConfigType, EditedDefinitionScope } from '../types';
import { messages } from './messages';
import { useDefinitionEditHistory } from './useDefinitionEditHistory';

const styles = cssMap({
	loadingSpinner: {
		display: 'flex',
		justifyContent: 'center',
		height: '100%',
		width: '100%',
	},
	editHistoryWrapper: {
		display: 'flex',
		flexDirection: 'column',
		paddingBottom: token('space.250'),
		gap: token('space.200'),
		font: token('font.body'),
	},
	editHistoryDescription: {
		color: token('color.text'),
		gap: token('space.050'),
	},
	editedDefinitionCardContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
	},
	editedDefinitionScopeText: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		color: token('color.text.subtlest'),
	},
	editedDefinitionCard: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-start',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		borderRadius: token('radius.small', '8px'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		gap: token('space.200'),
	},
	editedDefinitionAvatar: {
		borderRadius: token('radius.full', '64%'),
	},
	editedDefinitionCardBody: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		color: token('color.text'),
		font: token('font.body'),
		gap: token('space.150'),
	},
	editedDefinitionCardHeaderWrapper: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.0'),
	},
	editedDefinitionCardHeader: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.050'),
		color: token('color.text.subtle'),
		marginBottom: token('space.100'),
	},
	editedDefinitionCardEditor: {
		fontWeight: token('font.weight.semibold'),
		color: token('color.text'),
	},
	editedDefinitionCardReference: {
		display: 'flex',
		alignItems: 'center',
	},
	editedDefinitionCardReferenceTitle: {
		marginLeft: token('space.050'),
	},
	editedDefinitionCardHeaderSpacer: {
		width: '100%',
		color: token('color.text.disabled'),
		marginTop: token('space.025'),
		marginBottom: 0,
	},
});

const MarkdownRenderer = lazy(() =>
	import(/* webpackChunkName: "react-markdown" */ 'react-markdown').then(
		({ default: ReactMarkdown }) => {
			return {
				default: ReactMarkdown,
			};
		},
	),
);

const ScopeDescription = ({ scope }: { scope: EditedDefinitionScope }) => {
	const { formatMessage } = useIntl();
	let scopeDescription = '';
	let ScopeIcon;
	switch (scope) {
		case EditedDefinitionScope.ORGANIZATION:
			scopeDescription = formatMessage(messages.readingAidsEditHistoryVisibilityTextOrg);
			ScopeIcon = OfficeBuildingIcon;
			break;
		case EditedDefinitionScope.SPACE:
			scopeDescription = formatMessage(messages.readingAidsEditHistoryVisibilitySpace);
			ScopeIcon = DocumentsIcon;
			break;
		case EditedDefinitionScope.PAGE:
			scopeDescription = formatMessage(messages.readingAidsEditHistoryVisibilityPage);
			ScopeIcon = FileIcon;
			break;
		case EditedDefinitionScope.BLOGPOST:
			scopeDescription = formatMessage(messages.readingAidsEditHistoryVisibilityBlog);
			ScopeIcon = QuoteIcon;
			break;
		case EditedDefinitionScope.PROJECT:
			scopeDescription = formatMessage(
				FeatureGates.getExperimentValue('project-terminology-refresh', 'isEnabled', false)
					? messages.readingAidsEditHistoryVisibilityProjectGalaxia
					: messages.readingAidsEditHistoryVisibilityProject,
			);
			ScopeIcon = () => <OpenFolderIcon color={token('color.icon')} label="" />;
			break;
		default:
			return null;
	}
	return (
		<Inline xcss={styles.editedDefinitionScopeText} space="space.050">
			{scopeDescription} <ScopeIcon label="" />
		</Inline>
	);
};

type DefinitionEditHistoryProps = {
	curatedDefinitionScopeConfig: CuratedDefinitionScopeConfigType;
	onClose: () => void;
};

export const DefinitionEditHistory: ({
	curatedDefinitionScopeConfig,
	onClose,
}: DefinitionEditHistoryProps) => JSX.Element = ({
	curatedDefinitionScopeConfig,
	onClose,
}: DefinitionEditHistoryProps) => {
	const { formatMessage } = useIntl();
	const {
		commonAttributes: { query },
	} = useAIAnswerContext();
	const { definitions, loading } = useDefinitionEditHistory({
		query,
		curatedDefinitionScopeConfig,
	});
	const hasError = definitions.length === 0 && !loading;

	return (
		<Modal onClose={onClose} height={600}>
			<ModalHeader hasCloseButton>
				<ModalTitle>{formatMessage(messages.readingAidsEditHistoryHeader)}</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Stack xcss={styles.editHistoryWrapper}>
					<Inline xcss={styles.editHistoryDescription}>
						{formatMessage(messages.readingAidsEditHistoryDescription, {
							queryText: <b>{query}</b>,
						})}
					</Inline>
					{hasError || loading ? (
						<Box xcss={styles.loadingSpinner}>
							{loading && <Spinner size="large" testId="definition-history-loading-spinner" />}
							{hasError && (
								<EmptyState header={formatMessage(messages.readingAidsEditHistoryCardErrorText)} />
							)}
						</Box>
					) : (
						definitions.map((item, index) => (
							<Box
								xcss={styles.editedDefinitionCardContainer}
								key={`edited-definition-history-card-${index}`}
							>
								<ScopeDescription scope={item.scope} />
								<Box xcss={styles.editedDefinitionCard}>
									<Box xcss={styles.editedDefinitionAvatar}>
										{/* eslint-disable-next-line @atlassian/a11y/alt-text */}
										<img src={item.editor?.picture || ''} width="32px" height="32px" />
									</Box>
									<Box xcss={styles.editedDefinitionCardBody}>
										<Box xcss={styles.editedDefinitionCardHeaderWrapper}>
											<Inline xcss={styles.editedDefinitionCardHeader}>
												{formatMessage(messages.readingAidsEditHistoryCardEdited, {
													editorName: (
														<Inline xcss={styles.editedDefinitionCardEditor}>
															{item.editor?.name || ''}
														</Inline>
													),
												})}
												{' ∙ '}
												<TimeAgo date={Number(item.createdAt) * 1000} />
											</Inline>
											<hr role="presentation" css={styles.editedDefinitionCardHeaderSpacer} />
										</Box>
										<span data-testid={`rendered-markdown-definition-${index}`}>
											<Suspense fallback={<Fragment />}>
												{/*
													Preventing img elements from rendering due to security vulnerability: https://asecurityteam.atlassian.net/browse/VULN-1543153
													Followup ticket to re-enable img rendering: https://hello.jira.atlassian.cloud/browse/CCSEARCH-4799
												*/}
												<MarkdownRenderer
													disallowedElements={
														fg('hybrid_orchestrator_enable_image_search') ? undefined : ['img']
													}
												>
													{item.definition}
												</MarkdownRenderer>
											</Suspense>
										</span>
										{item.referenceUrl && item.confluenceEntity?.title && (
											<Box xcss={styles.editedDefinitionCardReference}>
												<Link appearance="subtle" target="_blank" href={item.referenceUrl}>
													{item.confluenceEntity.type === 'BLOG_POST' ? (
														<BlogObject label="" />
													) : (
														<PageObject label="" />
													)}
													<Inline xcss={styles.editedDefinitionCardReferenceTitle}>
														{item.confluenceEntity.title || ''}
													</Inline>
												</Link>
											</Box>
										)}
									</Box>
								</Box>
							</Box>
						))
					)}
				</Stack>
			</ModalBody>
			<ModalFooter />
		</Modal>
	);
};
